import i18n from '../../../../../../i18n';
import { METRICS, numberLocale } from '../../../../data/metrics';
import type { DriverBucket, DriverDimension } from '../../../../types';
import { dimFmt, higherIsBetter } from './driverUtils';

/**
 * The correlates tile in plain words. A rate reads "13%", a share "24%",
 * an average "74 min"; the compare period is "was 12%", never a
 * percentage of a percentage; and each dimension gets one sentence built
 * from the buckets the server marked significant, with the real figures
 * in it so the words can never say more than the numbers do.
 */

const t = (key: string, opts?: Record<string, unknown>): string =>
  String(i18n.t(`performance:drillUi.${key}`, opts ?? {}));

const has = (key: string): boolean => i18n.exists(`performance:drillUi.${key}`);

function num(v: number, digits: number): string {
  return v.toLocaleString(numberLocale(), { maximumFractionDigits: digits });
}

/** A rate as a whole percent, "13%"; "under 1%" for a trace. */
export function pctWords(pct: number): string {
  if (pct > 0 && pct < 0.5) return t('underOnePct');
  return t('pctValue', { n: Math.round(pct) });
}

/** An average in its unit: minutes, days, money, or the metric's format. */
export function avgWords(
  dim: DriverDimension,
  v: number,
  fmtVal: (n: number) => string,
  metricKey?: string,
): string {
  if (dim.unit === 'min') return t('minutesValue', { n: num(Math.round(v), 0) });
  if (metricKey === 'avgLeadTime') return t('daysValue', { n: num(v, 1) });
  return dimFmt(dim, v, fmtVal);
}

/** A bucket's share of its dimension, 0–100, this period. */
export function bucketShare(dim: DriverDimension, b: DriverBucket): number {
  if (b.sharePct != null) return b.sharePct;
  const total = dim.buckets.reduce((a, x) => a + Math.abs(x.value), 0);
  return total > 0 ? (Math.abs(b.value) / total) * 100 : 0;
}

/** A bucket's share of its dimension in the compare period, or null. */
export function bucketCompareShare(dim: DriverDimension, b: DriverBucket): number | null {
  if (b.compareValue == null) return null;
  if (dim.buckets.some(x => x.compareValue == null)) return null;
  const total = dim.buckets.reduce((a, x) => a + Math.abs(x.compareValue ?? 0), 0);
  return total > 0 ? (Math.abs(b.compareValue) / total) * 100 : 0;
}

/** The row's one number: "13%", "24%" or "74 min". */
export function bucketWords(
  dim: DriverDimension,
  b: DriverBucket,
  fmtVal: (n: number) => string,
  metricKey?: string,
): string {
  if (dim.mode === 'rate') return pctWords(b.value);
  if (dim.mode === 'share') return pctWords(bucketShare(dim, b));
  return avgWords(dim, b.value, fmtVal, metricKey);
}

/** The compare period's figure for the row, or null when it has none. */
export function bucketWasWords(
  dim: DriverDimension,
  b: DriverBucket,
  fmtVal: (n: number) => string,
  metricKey?: string,
): string | null {
  if (b.compareValue == null) return null;
  if (dim.mode === 'rate') return t('wasValue', { value: pctWords(b.compareValue) });
  if (dim.mode === 'share') {
    const share = bucketCompareShare(dim, b);
    return share == null ? null : t('wasValue', { value: pctWords(share) });
  }
  return t('wasValue', { value: avgWords(dim, b.compareValue, fmtVal, metricKey) });
}

export type ChangeTone = 'up-good' | 'up-bad' | 'down-good' | 'down-bad' | 'up' | 'down' | null;

/**
 * Whether the move since the compare period is worth an arrow, and which
 * way it reads: a rate or average moving against the metric's grain is bad;
 * a share moving is neither, so it gets a plain arrow.
 */
export function bucketChangeTone(dim: DriverDimension, b: DriverBucket): ChangeTone {
  if (b.compareValue == null) return null;
  let cur = b.value;
  let cmp = b.compareValue;
  if (dim.mode === 'share') {
    const s = bucketCompareShare(dim, b);
    if (s == null) return null;
    cur = bucketShare(dim, b);
    cmp = s;
  }
  const material =
    dim.mode === 'avg'
      ? cmp !== 0 && Math.abs(cur / cmp - 1) >= 0.05
      : Math.abs(cur - cmp) >= 1;
  if (!material) return null;
  const up = cur > cmp;
  if (dim.mode === 'share') return up ? 'up' : 'down';
  const good = up === higherIsBetter(dim);
  return up ? (good ? 'up-good' : 'up-bad') : good ? 'down-good' : 'down-bad';
}

/** "of reservations cancelled" for a rate's "13% …", empty when unknown. */
export function rateNoun(metricKey?: string): string {
  if (metricKey && has(`rateNoun.${metricKey}`)) return t(`rateNoun.${metricKey}`);
  return '';
}

/** What a share is a share of: "turns", "your occupancy", "guests". */
export function shareNoun(dim: DriverDimension, metricKey?: string): string {
  if (dim.shareOf && has(`shareNoun.${dim.shareOf}`)) return t(`shareNoun.${dim.shareOf}`);
  if (dim.shareOf) return dim.shareOf;
  return (METRICS[metricKey ?? '']?.short ?? '').toLowerCase();
}

/** "your average, 12% of reservations cancelled"; null for a share. */
export function baselineWords(
  dim: DriverDimension,
  fmtVal: (n: number) => string,
  metricKey?: string,
): string | null {
  if (dim.mode === 'share' || dim.baseline == null || dim.baseline <= 0) return null;
  return t('yourAverageIs', { value: averageWords(dim, fmtVal, metricKey) });
}

function averageWords(
  dim: DriverDimension,
  fmtVal: (n: number) => string,
  metricKey?: string,
): string {
  if (dim.mode === 'rate') {
    const noun = rateNoun(metricKey);
    return noun ? `${pctWords(dim.baseline)} ${noun}` : pctWords(dim.baseline);
  }
  return avgWords(dim, dim.baseline, fmtVal, metricKey);
}

/**
 * "about twice as many as Lunch", chosen from the exact ratio. Against a
 * figure that rounds to 1% or 2% a multiple would be false precision
 * ("14 times" of a 1.4% that prints as 1%), so it says "far more" instead.
 */
function ratioWords(ratio: number, other: string, bottomValue: number): string {
  if (ratio < 1.15) return t('ratioSame', { other });
  if (ratio >= 3.5 && bottomValue < 2.5) return t('ratioFar', { other });
  if (ratio < 1.75) return t('ratioMore', { other });
  if (ratio < 2.5) return t('ratioTwice', { other });
  if (ratio < 3.5) return t('ratioThrice', { other });
  return t('ratioTimes', { n: Math.round(ratio), other });
}

/**
 * The card's sentence. It only names buckets the server found significant
 * (`topDriver` above the average, `bottomDriver` below it, or the largest
 * and smallest share), and always prints their figures next to the words.
 */
export function dimHeadline(
  dim: DriverDimension,
  fmtVal: (n: number) => string,
  metricKey?: string,
): string | null {
  const top = dim.topDriver;
  const bottom = dim.bottomDriver;
  if (dim.mode === 'share') {
    if (!top) return null;
    const a = Math.round(bucketShare(dim, top));
    const noun = shareNoun(dim, metricKey);
    if (!bottom || bottom.label === top.label) {
      return t('headShareTopOnly', { top: top.label, a, noun });
    }
    const b = Math.round(bucketShare(dim, bottom));
    return t('headShare', { top: top.label, a, noun, bottom: bottom.label, b });
  }
  if (dim.mode === 'rate') {
    const noun = rateNoun(metricKey);
    const avg = pctWords(dim.baseline);
    if (top && bottom && top.label !== bottom.label) {
      const a = pctWords(top.value);
      const b = pctWords(bottom.value);
      if (bottom.value <= 0) {
        return t('headRateNone', { top: top.label, a, noun, bottom: bottom.label });
      }
      return t('headRate', {
        top: top.label,
        a,
        noun,
        ratio: ratioWords(top.value / bottom.value, bottom.label, bottom.value),
        b,
      });
    }
    if (top) return t('headRateTopOnly', { top: top.label, a: pctWords(top.value), noun, avg });
    if (bottom) {
      return t('headRateBottomOnly', { bottom: bottom.label, b: pctWords(bottom.value), noun, avg });
    }
    return t('headNone', { avg: noun ? `${avg} ${noun}` : avg });
  }
  // Average.
  const avg = avgWords(dim, dim.baseline, fmtVal, metricKey);
  if (top && bottom && top.label !== bottom.label) {
    return t('headAvg', {
      top: top.label,
      a: avgWords(dim, top.value, fmtVal, metricKey),
      bottom: bottom.label,
      b: avgWords(dim, bottom.value, fmtVal, metricKey),
    });
  }
  if (top) {
    return t('headAvgTopOnly', { top: top.label, a: avgWords(dim, top.value, fmtVal, metricKey), avg });
  }
  if (bottom) {
    return t('headAvgBottomOnly', {
      bottom: bottom.label,
      b: avgWords(dim, bottom.value, fmtVal, metricKey),
      avg,
    });
  }
  return t('headNone', { avg });
}

/** One line explaining the greyed rows, or null when none are grey. */
export function greyNote(dim: DriverDimension): string | null {
  const thin = dim.buckets.some(b => b.thin);
  const noise = dim.buckets.some(b => !b.thin && b.significant === false);
  if (thin && noise) return t('greyBoth');
  if (thin) return t('greyThin');
  if (noise) return t('greyNoise');
  return null;
}
